<template>
	<div v-bind="$attrs">
		<v-navigation-drawer
			permanent
			width="280"
			elevation="2"
			class="dactyl-nav-drawer"
		>
			<!-- Nav group open state (DCTL-2092). open-strategy="multiple" lets
			     groups toggle independently, so an expanded group is never collapsed
			     when the user navigates (Vuetify's default "list" opens the active
			     item's path and closes every other group on each route change).
			     `opened` is controlled so syncActiveGroup() can open the active
			     route's group on load + on navigation WITHOUT ever collapsing a group
			     the user opened - restoring the auto-open-active nicety "list" gave. -->
			<!-- DCTL-2113: key by nav shape so a classic-queue opt-out toggle fully
			     remounts the list instead of diffing grouped<->flat trees (which
			     briefly duplicates route-id nodes across the expand transition). -->
			<v-list
				:key="projectDashboardEnabled ? 'grouped' : 'flat'"
				v-model:opened="openedGroups"
				density="compact"
				nav
				open-strategy="multiple"
			>
				<v-list-item :class="{ 'px-0': mini }">
					<v-container class="d-flex align-center pl-0 pr-0 pt-3 pb-3">
						<v-avatar color="primary" size="48" class="mr-4 flex-shrink-0">
							<span class="text-white text-h5">{{ user.initials }}</span>
						</v-avatar>
						<v-container class="pa-0 min-width-0 overflow-hidden">
							<v-list-item-title>
								{{ user.first_name }} {{ user.last_name }}
							</v-list-item-title>
							<v-list-item-subtitle>
								{{ user.email }}
							</v-list-item-subtitle>
						</v-container>
					</v-container>
				</v-list-item>

				<!-- DCTL-2122: project-dashboard opt-in, positive framing. ON = new
				     per-project dashboard, OFF = classic global queue - the inverse of
				     the stored prefers_legacy_queue. Shown only while the base flag is on
				     AND the opt-out is still allowed (showLegacyToggle); flipping the
				     optout flag off removes it and forces everyone onto the new UI.
				     The turquoise "New" badge is a transient rollout nudge that disappears
				     after the first toggle interaction (dismissal stored per-device in
				     localStorage, see showNewBadge / setNewDashboardEnabled). -->
				<v-badge
					v-if="showLegacyToggle"
					:model-value="showNewBadge"
					color="accent"
					text-color="white"
					content="New"
					class="d-block px-4 pb-1"
				>
					<v-switch
						v-model="newDashboardEnabled"
						color="secondary"
						density="compact"
						hide-details
					>
						<template #label>
							<span class="text-body-2 ms-3"> New dashboard experience </span>
						</template>
					</v-switch>
				</v-badge>

				<v-divider class="mb-2" />

				<div v-for="item in menuItems" :key="item.title">
					<v-list-item
						v-if="!item.subGroup && showItem(item)"
						class="d-flex align-center pl-2 pr-2 pt-0 pb-0"
						color="secondary"
						:to="item.to"
					>
						<v-container class="d-flex align-center pl-0 pr-0 pt-3 pb-3">
							<v-icon class="mr-8">
								{{ item.icon }}
							</v-icon>
							<v-list-item-title>
								{{ item.title }}
							</v-list-item-title>
						</v-container>
					</v-list-item>

					<v-list-group
						v-else-if="item.subGroup && showItem(item)"
						:value="item.title"
						class="nav-group-container"
						append-icon="mdi-account-circle"
					>
						<template #activator="{ props }">
							<v-list-item
								v-bind="props"
								class="d-flex align-center pl-2 pr-2 pt-0 pb-0"
								:active="false"
								append-icon="mdi-chevron-down"
							>
								<v-container class="d-flex align-center pl-0 pr-0 pt-3 pb-3">
									<v-icon class="mr-8">
										{{ item.icon }}
									</v-icon>
									<v-list-item-title>
										{{ item.title }}
									</v-list-item-title>
								</v-container>
							</v-list-item>
						</template>

						<template v-for="subItem in item.subGroup" :key="subItem.title">
							<v-list-item
								v-if="showItem(subItem)"
								class="d-flex align-center pl-2 pr-2 pt-0 pb-0"
								color="secondary"
								:to="subItem.to"
								:disabled="subItem.disabled"
							>
								<v-container class="d-flex align-center pa-0">
									<v-icon class="mr-8">
										{{ subItem.icon }}
									</v-icon>
									<v-list-item-title>
										{{ subItem.title }}
									</v-list-item-title>
								</v-container>
							</v-list-item>
						</template>
					</v-list-group>
				</div>
			</v-list>
		</v-navigation-drawer>
		<v-container class="pa-6" fluid>
			<v-row align-content="start" justify="start" class="flex-nowrap">
				<v-col class="pa-0 flex-grow-1">
					<router-view />
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import { useLDFlag } from "launchdarkly-vue-client-sdk";

import { useProjectDashboardEnabled } from "@/composables/useProjectDashboardEnabled";

export default {
	name: "DactylHome",

	inheritAttrs: false,

	props: {
		login: {
			type: Boolean,
			default: false,
		},
		showBanner: {
			type: Boolean,
			default: false,
		},
	},

	emits: [
		"token",
		"enterDataEntry",
		"leaveDataEntry",
		"enterDataEntryTraining",
		"leaveDataEntryTraining",
		"enterDataEntryReadOnly",
		"leaveDataEntryReadOnly",
		"enterQualityControl",
		"leaveQualityControl",
		"activateActivityTimer",
		"deactivateActivityTimer",
		"checkDirty",
		"handleLogoutSave",
	],

	setup() {
		const qcDashboardEnabled = useLDFlag("qc-dashboard");
		const apiTokensEnabled = useLDFlag("api-tokens");
		const interviewManagementEnabled = useLDFlag(
			"interview-management-workflow",
		);
		// Release gate for the Activity Metrics admin page; defaults FALSE
		// when the LD environment does not define the flag (fail closed).
		const userActivityAdminPageEnabled = useLDFlag(
			"user-activity-admin-page",
			false,
		);
		// DCTL-2543: release gate for the Systematic Literature Search surface;
		// defaults FALSE when the LD environment does not define the flag (fail
		// closed), matching the backend flag:systematic-literature-search
		// middleware which 404s every endpoint.
		const literatureSearchEnabled = useLDFlag(
			"systematic-literature-search",
			false,
		);
		// DCTL-2113: the nav regroup follows the effective new-dashboard decision
		// (raw flag minus the per-user classic-queue opt-out), not the raw flag.
		const {
			enabled: projectDashboardEnabled,
			showLegacyToggle,
			prefersLegacyQueue,
		} = useProjectDashboardEnabled();

		return {
			qcDashboardEnabled,
			apiTokensEnabled,
			interviewManagementEnabled,
			userActivityAdminPageEnabled,
			literatureSearchEnabled,
			projectDashboardEnabled,
			showLegacyToggle,
			prefersLegacyQueue,
		};
	},

	data() {
		return {
			mini: false,
			// DCTL-2092: titles of the open nav groups (controlled v-list `opened`).
			// Seeded + kept in sync by syncActiveGroup(); user toggles flow back via
			// v-model:opened, so manual expand/collapse persists across navigation.
			openedGroups: [],
			// DCTL-2113: built by buildMenuItems() in created() and rebuilt whenever
			// the effective project-dashboard decision flips (e.g. the classic-queue
			// opt-out toggle), so the nav re-groups old<->new without a reload.
			menuItems: [],
			// DCTL-2122: per-device dismissal of the "New" rollout badge on the
			// dashboard toggle. Transient nudge, so localStorage - no backend. Set
			// on the first toggle interaction (see setNewDashboardEnabled).
			newDashboardBadgeDismissed:
				localStorage.getItem("newDashboardBadgeDismissed") === "true",
		};
	},

	computed: {
		user() {
			return useMainStore().user;
		},
		isAdmin() {
			return useMainStore().isAdmin;
		},
		userPermissions() {
			return useMainStore().userPermissions;
		},
		// DCTL-2122: the drawer switch is framed positively (ON = new dashboard),
		// so its model is the inverse of the persisted classic-queue opt-out.
		newDashboardEnabled: {
			get() {
				return !this.prefersLegacyQueue;
			},
			set(value) {
				this.setNewDashboardEnabled(value);
			},
		},
		// DCTL-2122: show the "New" nudge only while the toggle itself is shown and
		// the user hasn't interacted with it yet on this device.
		showNewBadge() {
			return this.showLegacyToggle && !this.newDashboardBadgeDismissed;
		},
		firstAvailableRoute() {
			for (const item of this.menuItems) {
				if (item.subGroup) {
					// Return the first child the user can actually SEE. A group's
					// permissions are the OR-union of its children, so subGroup[0]'s
					// own gate may be stricter than the group's - landing there could
					// bounce the user off a route they cannot access. showItem also
					// honors per-item adminBypass. (DCTL-2043)
					const visibleChild = item.subGroup.find((sub) => this.showItem(sub));
					if (visibleChild) return visibleChild.to;
				} else if (this.showItem(item)) {
					return item.to;
				}
			}

			return "/home/dashboard";
		},
	},

	watch: {
		// DCTL-2092: keep the active route's nav group open on every navigation.
		// Only ever ADDS it (open-strategy="multiple"), so a group the user
		// expanded is never collapsed.
		$route() {
			this.syncActiveGroup();
		},
		// DCTL-2113: rebuild the nav when the effective project-dashboard decision
		// flips (the classic-queue opt-out toggle), so the nav re-groups old<->new
		// without a reload. syncActiveGroup re-opens the current route's group.
		projectDashboardEnabled() {
			this.buildMenuItems();
			this.syncActiveGroup();
		},
		// Same rebuild when the Activity Metrics release gate streams a new
		// value, so the entry appears or vanishes without a reload.
		userActivityAdminPageEnabled() {
			this.buildMenuItems();
			this.syncActiveGroup();
		},
		// DCTL-2543: same again for the Systematic Literature Search release gate.
		literatureSearchEnabled() {
			this.buildMenuItems();
			this.syncActiveGroup();
		},
	},

	created() {
		this.buildMenuItems();

		// Redirect to first available route if on base /home route (once, on
		// mount). Unlike the nav rebuild this must NOT re-run on every toggle.
		if (this.$route.path === "/home" || this.$route.path === "/home/") {
			this.$router.replace(this.firstAvailableRoute);
		}

		// DCTL-2092: open the group that owns the current route on first load;
		// the $route watcher keeps it in sync afterwards. (On the /home redirect
		// above the path is still /home here, so nothing matches yet - the watcher
		// handles the group once the replace resolves.)
		this.syncActiveGroup();
	},

	methods: {
		...mapActions(useMainStore, [
			"showNotification",
			"setUser",
			"setPrefersLegacyQueue",
		]),

		// DCTL-2113: (re)build the nav from the flat base list, applying the flag-
		// gated regrouping. Runs on mount and whenever the effective project-
		// dashboard decision flips (the classic-queue opt-out), so the nav re-
		// groups old<->new live without a reload.
		buildMenuItems() {
			this.menuItems = this.baseMenuItems();

			if (this.qcDashboardEnabled) {
				this.menuItems.splice(2, 0, {
					permissions: ["quality_control"],
					icon: "mdi-eye-check",
					title: "Quality Control",
					subGroup: [
						{
							permissions: ["quality_control"],
							icon: "mdi-view-column",
							title: "QC Dashboard",
							to: "/home/quality-control-dashboard",
						},
						{
							permissions: ["quality_control"],
							icon: "mdi-check-decagram",
							title: "Verified Data",
							to: "/home/quality-control-verified",
						},
					],
				});
			}

			if (this.projectDashboardEnabled) {
				// DCTL-2041: behind the project-dashboard flag, regroup the flat nav into
				// concern-based groups (Source Data Extraction / Terminology /
				// Administration), mirroring the existing Quality Control and Project
				// Management groups. When the flag is OFF this block does not run and the
				// flat legacy nav built above is left untouched.
				//
				// Each group parent carries the OR-union of its children's permissions so
				// the group shows whenever any child is visible; per-child visibility is
				// handled by showItem(subItem) in the template, so mixed-gate groups
				// (e.g. Terminology) populate partially per user.

				// Source Data Extraction: the SDE dashboard plus the training and
				// completed-extraction views.
				const sourceDataExtractionGroup = {
					permissions: [
						["source_data_extraction"],
						["quality_control"],
						["sde_training"],
						["access_completed_extractions"],
					],
					icon: "mdi-keyboard",
					title: "Source Data Extraction",
					subGroup: [
						{
							permissions: [["source_data_extraction"], ["quality_control"]],
							icon: "mdi-view-column",
							title: "SDE Dashboard",
							to: "/home/dashboard",
						},
						{
							permissions: [
								["source_data_extraction"],
								["quality_control"],
								["sde_training"],
							],
							icon: "mdi-school",
							title: "Training",
							to: "/home/training-dashboard",
						},
						{
							permissions: [["source_data_extraction"], ["quality_control"]],
							icon: "mdi-account-check",
							title: "My Completed Extractions",
							to: "/home/my-completed-extractions",
						},
						{
							permissions: ["access_completed_extractions"],
							icon: "mdi-clipboard-check-multiple",
							title: "Completed Extractions",
							to: "/home/completed-extractions",
						},
					],
				};

				// Projects (DCTL-2055): expandable group hosting Project Management
				// (per-project Settings + New Project, was the standalone "Projects"
				// leaf) and the Orphaned Papers safety-net list. Group parent perms are
				// the OR-union of its children, so the group shows whenever either
				// child is visible and per-child showItem handles the rest. Stays
				// 2-level per the subGroup convention.
				//
				// DCTL-2484: Project Management is reachable with manage_projects OR
				// download_data, matching its route guard - a download_data holder goes
				// there for the verified-data export (DCTL-2482) and every
				// project-management control on the page is gated separately. The
				// nested-array form is userHasPermission's existing OR-of-AND-sets
				// shape (see terminologyGroup). Orphaned Papers stays manage_projects
				// only (DCTL-2048), so the group's OR-union widens with its widest
				// child.
				const projectsItem = {
					permissions: [["manage_projects"], ["download_data"]],
					adminBypass: true,
					icon: "mdi-folder-multiple",
					title: "Projects",
					subGroup: [
						{
							permissions: [["manage_projects"], ["download_data"]],
							adminBypass: true,
							icon: "mdi-folder",
							title: "Project Management",
							to: "/home/project-management",
						},
						// DCTL-2048 (Phase 4): the former "Paper Management" leaf is now
						// the orphaned-papers safety-net list (papers with no project). It
						// reuses the /home/paper-manager route, whose component renders the
						// orphan list when this flag is on. Add-paper is no longer here: it
						// moved into the project context (DCTL-2035). Gated on
						// manage_projects (administrators bypass) to match the endpoint +
						// route guard - widened from the legacy upload_file/delete_file so
						// group leads (who hold manage_projects but not upload_file) can
						// reach it.
						{
							permissions: ["manage_projects"],
							adminBypass: true,
							icon: "mdi-file-document-alert",
							title: "Orphaned Papers",
							to: "/home/paper-manager",
							// DCTL-2059: disabled when there are zero orphaned papers (the
							// safety-net list is expected to be empty). Set from the count
							// fetch below.
							disabled: false,
						},
					],
				};

				// Terminology: the concepts browser plus the former Tag Management
				// group's children, flattened to stay 2-level. Mixed gates
				// (access_completed_extractions vs manage_tags) - handled per-child.
				const terminologyGroup = {
					permissions: [
						["access_completed_extractions"],
						["manage_tags"],
						["manage_units"],
					],
					icon: "mdi-tag-multiple",
					title: "Terminology",
					subGroup: [
						{
							permissions: ["access_completed_extractions"],
							icon: "mdi-book-open-variant",
							title: "Dactyl Concepts",
							to: "/home/dactyl-concepts",
						},
						{
							// DCTL-2191: whole page gated on manage_units (admins
							// bypass, group_lead holds the grant). The group-level
							// permissions union above includes manage_units so a
							// group-lead-only user still sees this group + child.
							permissions: ["manage_units"],
							icon: "mdi-ruler",
							title: "Preferred Units",
							to: "/home/preferred-units",
						},
						{
							permissions: ["manage_tags"],
							icon: "mdi-tag-text",
							title: "Tag Groups",
							to: "/home/tag/tag-group-manager",
						},
						{
							permissions: ["manage_tags"],
							icon: "mdi-tag",
							title: "Base Tags",
							to: "/home/tag/base-tag-manager",
						},
					],
				};

				// Administration: user and organization management, plus the SDEv2
				// activity metrics reporting page. Activity Metrics is release-gated
				// behind the user-activity-admin-page LD flag (metrics gathering
				// ships before the view page) and admin-bypassed like the route
				// guard, so admins see the entry the guard would let them open.
				const administrationGroup = {
					permissions: [
						["create_user", "edit_user", "delete_user"],
						["manage_organizations"],
						...(this.userActivityAdminPageEnabled
							? [["view_activity_metrics"]]
							: []),
					],
					adminBypass: true,
					icon: "mdi-shield-account",
					title: "Administration",
					subGroup: [
						{
							permissions: ["create_user", "edit_user", "delete_user"],
							icon: "mdi-account-supervisor",
							title: "User Management",
							to: "/home/user-manager",
						},
						{
							permissions: ["manage_organizations"],
							icon: "mdi-office-building-cog",
							title: "Organizations",
							to: "/home/organizations",
						},
						...(this.userActivityAdminPageEnabled
							? [
									{
										permissions: ["view_activity_metrics"],
										adminBypass: true,
										icon: "mdi-chart-timeline-variant",
										title: "Activity Metrics",
										to: "/home/activity-metrics",
									},
								]
							: []),
					],
				};

				// The Quality Control group is spliced in above only when qc-dashboard is
				// enabled; preserve that reference so it keeps its position.
				const qualityControlGroup = this.menuItems.find(
					(item) => item.title === "Quality Control",
				);

				// Rebuild the menu in the target top-level order. Interview Management and
				// personal Settings are appended by the blocks below: Organizations is now
				// a child, so the interview block's findIndex falls through to a push,
				// landing it after Administration (the intended position).
				this.menuItems = [
					sourceDataExtractionGroup,
					...(qualityControlGroup ? [qualityControlGroup] : []),
					projectsItem,
					terminologyGroup,
					administrationGroup,
				];

				// DCTL-2059: disable the Orphaned Papers entry when there are no orphans.
				// Only fetch when the user can actually reach it (manage_projects, admins
				// bypass) - matches the leaf's own showItem gate. Non-critical: if the
				// count can't be loaded the item just stays enabled.
				const orphanedPapersNavItem = projectsItem.subGroup.find(
					(child) => child.to === "/home/paper-manager",
				);
				if (orphanedPapersNavItem && this.showItem(orphanedPapersNavItem)) {
					this.$http
						.get("/papers/orphaned")
						.then((response) => {
							orphanedPapersNavItem.disabled =
								(response.data.papers || []).length === 0;
						})
						.catch(() => {});
				}
			}

			// DCTL-2543: Systematic Literature Search entry, hidden while the
			// systematic-literature-search LD flag is off (the backend 404s the
			// whole surface then anyway). One insert that works in both nav shapes:
			// after the Projects group when the grouped nav is on, after the flat
			// Paper Management leaf otherwise; falls through to a push when neither
			// anchor exists.
			if (this.literatureSearchEnabled) {
				const literatureSearchItem = {
					permissions: ["search_literature"],
					adminBypass: true,
					icon: "mdi-book-search",
					title: "Systematic Literature Search",
					to: "/home/systematic-literature-search",
				};
				const anchorIndex = this.menuItems.findIndex((item) =>
					this.projectDashboardEnabled
						? item.title === "Projects"
						: item.to === "/home/paper-manager",
				);
				this.menuItems.splice(
					anchorIndex !== -1 ? anchorIndex + 1 : this.menuItems.length,
					0,
					literatureSearchItem,
				);
			}

			if (this.interviewManagementEnabled) {
				const orgIndex = this.menuItems.findIndex(
					(item) => item.to === "/home/organizations",
				);
				const insertAt = orgIndex !== -1 ? orgIndex : this.menuItems.length;
				this.menuItems.splice(insertAt, 0, {
					permissions: ["manage_interviews"],
					icon: "mdi-account-question",
					title: "Interview Management",
					to: "/home/interview-batches",
				});
			}

			if (this.apiTokensEnabled) {
				// only show settings (password reset) if not SSO or if admin
				if (!this.user.sso_enabled || this.isAdmin) {
					this.menuItems.push({
						permissions: ["edit_user_self"],
						icon: "mdi-account-cog",
						title: "Settings",
						to: "/home/settings",
					});
				}
			} else {
				// only show settings (password reset) if not SSO
				if (!this.user.sso_enabled) {
					this.menuItems.push({
						permissions: ["edit_user_self"],
						icon: "mdi-account-cog",
						title: "Settings",
						to: "/home/settings",
					});
				}
			}
		},

		// DCTL-2113: the flat legacy nav (the flag-off shape). Returned fresh on
		// each call so buildMenuItems() always re-derives the grouped nav from a
		// clean base.
		baseMenuItems() {
			return [
				{
					permissions: [["source_data_extraction"], ["quality_control"]],
					icon: "mdi-keyboard",
					title: "Source Data Extraction",
					to: "/home/dashboard",
				},
				{
					permissions: [
						["source_data_extraction"],
						["quality_control"],
						["sde_training"],
					],
					icon: "mdi-school",
					title: "Training",
					to: "/home/training-dashboard",
				},
				{
					permissions: [["source_data_extraction"], ["quality_control"]],
					icon: "mdi-account-check",
					title: "My Completed Extractions",
					to: "/home/my-completed-extractions",
				},
				{
					permissions: ["access_completed_extractions"],
					icon: "mdi-clipboard-check-multiple",
					title: "Completed Extractions",
					to: "/home/completed-extractions",
				},
				{
					permissions: ["delete_file", "upload_file"],
					icon: "mdi-briefcase",
					title: "Project Management",
					to: "/home/project-manager",
				},
				{
					permissions: ["delete_file", "upload_file"],
					icon: "mdi-file-document-multiple",
					title: "Paper Management",
					to: "/home/paper-manager",
				},
				{
					permissions: ["manage_tags"],
					icon: "mdi-tag-edit",
					title: "Tag Management",
					subGroup: [
						{
							permissions: ["manage_tags"],
							icon: "mdi-tag-text",
							title: "Tag Groups",
							to: "/home/tag/tag-group-manager",
						},
						{
							permissions: ["manage_tags"],
							icon: "mdi-tag",
							title: "Base Tags",
							to: "/home/tag/base-tag-manager",
						},
					],
				},
				{
					permissions: ["create_user", "edit_user", "delete_user"],
					icon: "mdi-account-supervisor",
					title: "User Management",
					to: "/home/user-manager",
				},
				{
					permissions: ["manage_organizations"],
					icon: "mdi-office-building-cog",
					title: "Organizations",
					to: "/home/organizations",
				},
				// Release-gated behind the user-activity-admin-page LD flag and
				// admin-bypassed like the route guard (flag AND (permission OR
				// admin)).
				...(this.userActivityAdminPageEnabled
					? [
							{
								permissions: ["view_activity_metrics"],
								adminBypass: true,
								icon: "mdi-chart-timeline-variant",
								title: "Activity Metrics",
								to: "/home/activity-metrics",
							},
						]
					: []),
				{
					permissions: ["access_completed_extractions"],
					icon: "mdi-book-open-variant",
					title: "Metathesaurus",
					to: "/home/metathesaurus",
				},
			];
		},

		// DCTL-2122: apply the new-dashboard switch (enabled = show the new
		// per-project dashboard). The stored preference is the inverse opt-out, so
		// prefers_legacy_queue = !enabled. Optimistically updates the store so the
		// dashboard + nav re-render immediately, then persists via PATCH and
		// rehydrates the returned user; reverts on failure.
		setNewDashboardEnabled(enabled) {
			// DCTL-2122: any toggle interaction retires the "New" rollout nudge for
			// good on this device. Tied to the interaction, not to prefers_legacy_queue
			// (false there is ambiguous: never-touched vs. toggled back on).
			this.newDashboardBadgeDismissed = true;
			localStorage.setItem("newDashboardBadgeDismissed", "true");

			const prefersLegacyQueue = !enabled;
			this.setPrefersLegacyQueue(prefersLegacyQueue);
			this.$http
				.patch("/users/preferences", { prefersLegacyQueue })
				.then((response) => {
					this.setUser(response.data.payload.user);
				})
				.catch(() => {
					// revert the optimistic change on failure
					this.setPrefersLegacyQueue(!prefersLegacyQueue);
					this.showNotification({
						message: "Could not update your view preference. Please try again.",
						status: "error",
					});
				});
		},

		// Whether a nav item should be shown. adminBypass items (e.g. the
		// manage_projects dashboard) are visible to administrators too, matching
		// how the route guard and backend already let admins through can:.
		showItem(item) {
			return (
				(item.adminBypass && this.isAdmin) ||
				this.userHasPermission(item.permissions)
			);
		},
		userHasPermission(permissions) {
			if (Array.isArray(permissions[0])) {
				// or-logic required
				return permissions.some((set) =>
					set.every((permission) => this.userPermissions[permission]),
				);
			}

			return permissions.every(
				(permission) => this.userPermissions[permission],
			);
		},

		// DCTL-2092: open the nav group whose sub-item matches the current route.
		// Only ever ADDS it to openedGroups - open-strategy="multiple" leaves the
		// user's other open groups alone - so this restores the auto-open-active
		// behaviour without collapsing anything the user expanded.
		syncActiveGroup() {
			const path = this.$route.path;
			const group = this.menuItems.find((item) =>
				item.subGroup?.some(
					(sub) => sub.to && (path === sub.to || path.startsWith(sub.to + "/")),
				),
			);
			if (group && !this.openedGroups.includes(group.title)) {
				this.openedGroups.push(group.title);
			}
		},
	},
};
</script>

<style lang="scss">
.deleted {
	td {
		color: #999;
		text-decoration: line-through;
		text-decoration-thickness: 1px;
	}

	td:last-of-type {
		opacity: 1;
		text-decoration: none;
	}
}

.dactyl-nav-drawer {
	height: auto !important;
	border-radius: 4px;
}

.nav-group-container > .v-list-group__header {
	// get rid of the extra bottom margin on v-list-group in the side nav
	margin-bottom: 0 !important;

	.v-list-item__content {
		flex-grow: 1;
	}
}
</style>
